/**
 * Slideshow Component - Swiper-based slider component
 * Automatically initializes Swiper for slideshow-container-item elements
 */

class SlideshowContainer extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.observer = null;
    this._initialized = false;
  }

  connectedCallback() {
    const typeMobile = this.getAttribute('data-type-mobile');
    const typeDesktop = this.getAttribute('data-type-desktop');
    const isMobile = window.innerWidth < 768;
    if (typeMobile === 'grid' && isMobile) {
      this.dataset.initialized = 'true';
      // console.log('typeMobile');
      this._initialized = true;
      return;
    }
    if (typeDesktop === 'grid' && !isMobile) {
      this.dataset.initialized = 'true';
      // console.log('typeDesktop');
      this._initialized = true;
      return;
    }

    if (!this._initialized) {
      this.init();
      this._initialized = true;
      // console.log(this, 'initialized');
    }
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.waitForSwiper();
      });
    } else {
      this.waitForSwiper();
    }
  }

  waitForSwiper() {
    // Wait for Swiper library to be available
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.waitForSwiper(), 100);
      return;
    }

    // Small delay to ensure DOM is fully rendered
    setTimeout(() => {
      if (this.isConnected) {
        this.setupSwiper();
        //this.observeItems();
      }
    }, 50);
  }

  setupSwiper() {
    let sliderConfig = {};
    try {
      const configScript = this.querySelector('script[type="application/json"]');
      if (configScript) {
        sliderConfig = JSON.parse(configScript.textContent);
      }
    } catch (e) {
      console.error(
        'Slideshow Component: Failed to parse slider config JSON. Using defaults fallback.',
        e
      );
      sliderConfig = {};
    }

    // Check if desktop only
    const desktopOnly =
      sliderConfig.desktopOnly !== undefined
        ? sliderConfig.desktopOnly
        : this.getAttribute('data-desktop-only') === 'true';
    if (desktopOnly && window.innerWidth < 768) {
      return;
    }

    // Separate custom events before spreading so we don't clobber inner Swiper bindings
    const customEvents = sliderConfig.on || {};
    delete sliderConfig.on;
    // Desktop only is not a native swiper config property so remove it as well.
    delete sliderConfig.desktopOnly;

    // Get all slideshow-container-item elements (direct children or nested)
    let items = Array.from(this.querySelectorAll('slideshow-container-item'));

    // the active loop value is either from sliderConfig natively or the fallback
    const isLoopEnabled = sliderConfig.loop === true;

    // Create swiper wrapper if it doesn't exist
    let wrapper = this.querySelector('.swiper-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'swiper-wrapper';
      this.appendChild(wrapper);
    }

    if (
      typeof window.theme === 'undefined' ||
      window.theme.settings?.enable_carousel_accessibility !== false
    ) {
      this.setupContainerAccessibility();
      this.setupWrapperAccessibility(wrapper);
      this.setupSlideAccessibility();
    }

    // Swiper loop needs enough slides to work correctly.
    // If we have few items, we clone them to ensure loop stability.
    if (isLoopEnabled && items.length > 0 && items.length < 6) {
      const originalItems = [...items];
      const timesToClone = Math.ceil(6 / items.length) - 1; // Subtract 1 because original exists
      for (let i = 0; i < timesToClone; i++) {
        originalItems.forEach((item) => {
          const clone = item.cloneNode(true);
          // If the item itself has swiper-slide class, we wrap it if it's not already
          wrapper.appendChild(clone);
        });
      }
      items = Array.from(this.querySelectorAll('slideshow-container-item'));
    }

    // Move all slideshow-container-item elements into wrapper if not already there
    items.forEach((item) => {
      // Case 1: Item is already a swiper-slide child of the wrapper
      if (item.parentNode === wrapper && item.classList.contains('swiper-slide')) {
        return;
      }

      // Case 2: Item is in the wrapper but missing the class
      if (item.parentNode === wrapper) {
        item.classList.add('swiper-slide');
        return;
      }

      // Case 3: Item is outside the wrapper, move it in
      if (item.classList.contains('swiper-slide')) {
        wrapper.appendChild(item);
      } else {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.appendChild(item);
        wrapper.appendChild(slide);
      }
    });

    // Show element before initialization to allow dimension calculations
    this.style.display = 'block';

    // Safety fallback: force initialized status if it takes too long
    setTimeout(() => {
      if (!this.dataset.initialized) {
        this.dataset.initialized = 'true';
        if (parent) {
          const skeleton = parent.querySelector('.skeleton-wrapper');
          if (skeleton) skeleton.style.display = 'none';
        }
      }
    }, 5000);

    // Add navigation buttons if they don't exist and not explicitly hidden
    if (sliderConfig.navigation !== false) {
      let nextButton = this.querySelector('.swiper-button-next');
      let prevButton = this.querySelector('.swiper-button-prev');

      if (!nextButton) {
        nextButton = document.createElement('div');
        nextButton.className = 'swiper-button-next';
        this.appendChild(nextButton);
      }
      if (!prevButton) {
        prevButton = document.createElement('div');
        prevButton.className = 'swiper-button-prev';
        this.appendChild(prevButton);
      }

      if (
        typeof window.theme === 'undefined' ||
        window.theme.settings?.enable_carousel_accessibility !== false
      ) {
        this.setupControlsAccessibility(prevButton, nextButton, wrapper);
      }

      if (!nextButton.querySelector('.screen-reader-only')) {
        const span = document.createElement('span');
        span.className = 'screen-reader-only';
        span.textContent = 'Next';
        nextButton.appendChild(span);
      }
      if (!prevButton.querySelector('.screen-reader-only')) {
        const span = document.createElement('span');
        span.className = 'screen-reader-only';
        span.textContent = 'Previous';
        prevButton.appendChild(span);
      }

      if (!sliderConfig.navigation) {
        sliderConfig.navigation = {
          nextEl: nextButton,
          prevEl: prevButton,
        };
      }
    }

    // Add pagination if it doesn't exist and not explicitly hidden
    if (sliderConfig.pagination !== false) {
      if (!this.querySelector('.swiper-pagination')) {
        const pagination = document.createElement('div');
        pagination.className = 'swiper-pagination';
        this.appendChild(pagination);
      }

      if (!sliderConfig.pagination) {
        sliderConfig.pagination = {
          el: this.querySelector('.swiper-pagination'),
          clickable: true,
          dynamicBullets: false,
        };
      }

      if (typeof sliderConfig.pagination === 'object') {
        if (!sliderConfig.pagination.renderBullet) {
          sliderConfig.pagination.renderBullet = function (index, className) {
            return `<button type="button" class="${className}" aria-label="${index + 1}"></button>`;
          };
        }
      }
    }

    // Assign core default settings that Swiper needs to function properly inside this component
    const coreSettings = {
      centerInsufficientSlides: false,
      watchOverflow: true,
      watchSlidesProgress: true,
      observeParents: true,
      observer: true,
      autoHeight: true,
      speed: 600,
      slidesPerGroup: 1,
      a11y: false,
    };

    // Initialize Swiper logic via spread JSON data overriding core settings
    this.swiper = new Swiper(this, {
      ...coreSettings,
      ...sliderConfig,
      on: {
        init: () => {
          this.dataset.initialized = 'true';
          this.dispatchEvent(
            new CustomEvent('swiper:init', {
              detail: { swiper: this.swiper },
              bubbles: true,
              composed: true,
            })
          );
          requestAnimationFrame(() => {
            if (this.swiper) {
              this.swiper.update();
              this.swiper.updateSlides();
              this.swiper.updateAutoHeight();
              if (window.theme?.settings?.enable_carousel_accessibility !== false) {
                this.setupSlideAccessibility();
              }
              // Always run — preventing focus in hidden slides is mandatory (WCAG 2.1.1),
              // not an optional enhancement controlled by theme settings.
              this.setupSlideVisibility();
              this.setupPaginationAccessibility();
              this.setupFocusScroll();
            }
          });

          this.initPlayPause();

          if (customEvents.init) {
            const initFn = customEvents.init;
            if (typeof initFn === 'string' && typeof window[initFn] === 'function') {
              window[initFn].call(this);
            } else if (typeof initFn === 'function') {
              initFn.call(this);
            }
          }
        },
        slideChange: () => {
          this.dispatchEvent(
            new CustomEvent('swiper:slideChange', {
              detail: { swiper: this.swiper },
              bubbles: true,
              composed: true,
            })
          );

          this.setupSlideVisibility();
          this.setupPaginationAccessibility();

          if (customEvents.slideChange) {
            const slideChangeFn = customEvents.slideChange;
            if (typeof slideChangeFn === 'string' && typeof window[slideChangeFn] === 'function') {
              window[slideChangeFn].call(this);
            } else if (typeof slideChangeFn === 'function') {
              slideChangeFn.call(this);
            }
          }
        },
        paginationRender: () => {
          this.setupPaginationAccessibility();
        },
        paginationUpdate: () => {
          this.setupPaginationAccessibility();
        },
        slideChangeTransitionStart: () => {
          this.dispatchEvent(
            new CustomEvent('swiper:slideChangeTransitionStart', {
              detail: { swiper: this.swiper },
              bubbles: true,
              composed: true,
            })
          );
          this.setupSlideVisibility();
          this.setupPaginationAccessibility();
        },
        transitionEnd: () => {
          // Re-run after animation completes so swiper-slide-visible classes
          // are fully settled before we lock down tabindex on hidden slides.
          this.setupSlideVisibility();
          this.setupPaginationAccessibility();
        },
      },
    });
  }

  observeItems() {
    this.observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          Array.from(mutation.addedNodes).forEach((node) => {
            if (
              node.nodeType === 1 &&
              (node.tagName === 'SLIDESHOW-CONTAINER-ITEM' ||
                node.querySelector('slideshow-container-item'))
            ) {
              if (!node.closest('.swiper-slide')) {
                shouldUpdate = true;
              }
            }
          });
        }
      });
      if (shouldUpdate) {
        this.updateSwiper();
      }
    });

    this.observer.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  updateSwiper() {
    if (!this.swiper) {
      this.setupSwiper();
      return;
    }
    const wrapper = this.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const items = Array.from(this.querySelectorAll('slideshow-container-item'));
    items.forEach((item) => {
      if (!item.closest('.swiper-slide')) {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        item.parentNode.insertBefore(slide, item);
        slide.appendChild(item);
      }
    });

    setTimeout(() => {
      if (this.swiper) {
        this.swiper.update();
        this.swiper.updateSlides();
        this.swiper.updateSlidesClasses();
        if (window.theme?.settings?.enable_carousel_accessibility !== false) {
          this.setupSlideAccessibility();
          this.setupSlideVisibility();
        }
      }
    }, 100);
  }

  initPlayPause() {
    if (!this.swiper || !this.swiper.autoplay) return;

    // Add play/pause toggle if button exists in parent
    const pauseBtn = this.parentElement.querySelector('[data-slideshow-pause]');
    if (!pauseBtn) return;

    const playPauseWrap = pauseBtn.closest('.homepage-slider-play-pause');
    if (playPauseWrap) {
      playPauseWrap.classList.remove('hidden');
    }

    pauseBtn.addEventListener('click', () => {
      pauseBtn.classList.toggle('active');
      if (pauseBtn.classList.contains('active')) {
        this.swiper.autoplay.stop();
        pauseBtn.setAttribute(
          'aria-label',
          pauseBtn.getAttribute('aria-label')?.replace('pause', 'play') || 'Play'
        );
      } else {
        this.swiper.autoplay.start();
        pauseBtn.setAttribute(
          'aria-label',
          pauseBtn.getAttribute('aria-label')?.replace('play', 'pause') || 'Pause'
        );
      }
    });
  }

  setupContainerAccessibility() {
    if (window.theme?.settings?.enable_carousel_accessibility === false) return;

    this.setAttribute('role', 'region');
    if (!this.hasAttribute('aria-roledescription')) {
      this.setAttribute('aria-roledescription', 'carousel');
    }
    if (!this.hasAttribute('aria-label') && !this.hasAttribute('aria-labelledby')) {
      const section =
        this.closest('.shopify-section') || this.closest('section') || this.parentElement;
      let headingText = '';
      if (section) {
        const heading = section.querySelector(
          'h1, h2, h3, h4, h5, h6, .section-header__title, .section-title, .title, .heading, .box-title'
        );
        if (heading) {
          headingText = heading.textContent.trim().replace(/\s+/g, ' ');
        }
      }
      if (!headingText) {
        let sibling = this.previousElementSibling;
        while (sibling) {
          const heading =
            sibling.querySelector(
              'h1, h2, h3, h4, h5, h6, .section-header__title, .section-title, .title, .heading, .box-title'
            ) || (sibling.matches('h1, h2, h3, h4, h5, h6') ? sibling : null);
          if (heading) {
            headingText = heading.textContent.trim().replace(/\s+/g, ' ');
            break;
          }
          sibling = sibling.previousElementSibling;
        }
      }
      if (headingText) {
        this.setAttribute('aria-label', headingText);
      }
    }
  }

  setupWrapperAccessibility(wrapper) {
    if (window.theme?.settings?.enable_carousel_accessibility === false || !wrapper) return;

    if (!wrapper.id) {
      wrapper.id = `carousel-items-${Math.random().toString(36).substr(2, 9)}`;
    }
    wrapper.setAttribute('aria-live', 'off');
  }

  setupControlsAccessibility(prevButton, nextButton, wrapper) {
    if (window.theme?.settings?.enable_carousel_accessibility === false) return;

    if (nextButton) {
      nextButton.setAttribute('role', 'button');
      nextButton.setAttribute('tabindex', '0');
      nextButton.setAttribute('aria-label', 'Next slide');
      nextButton.setAttribute('title', 'Next slide');
    }

    if (prevButton) {
      prevButton.setAttribute('role', 'button');
      prevButton.setAttribute('tabindex', '0');
      prevButton.setAttribute('aria-label', 'Previous slide');
      prevButton.setAttribute('title', 'Previous slide');
    }
  }

  setupSlideAccessibility() {
    if (window.theme?.settings?.enable_carousel_accessibility === false) return;

    const slides = Array.from(this.querySelectorAll('.swiper-slide'));
    const totalSlides = slides.length;
    slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      if (!slide.hasAttribute('aria-roledescription')) {
        slide.setAttribute('aria-roledescription', 'slide');
      }
      if (!slide.hasAttribute('aria-label')) {
        slide.setAttribute('aria-label', `${index + 1} of ${totalSlides}`);
      }

      // Neutralize nested article landmark roles inside carousel slides
      // const nestedCards = slide.querySelectorAll('article, .product-card');
      // nestedCards.forEach((card) => {
      //   card.setAttribute('role', 'presentation');
      // });
    });
  }

  setupSlideVisibility() {
    if (window.theme?.settings?.enable_carousel_accessibility === false) return;

    const slides = Array.from(this.querySelectorAll('.swiper-slide'));
    slides.forEach((slide) => {
      const isVisible = slide.classList.contains('swiper-slide-visible');
      if (isVisible) {
        slide.removeAttribute('aria-hidden');
        slide.querySelectorAll('a, button, select, input, textarea, [tabindex]').forEach((el) => {
          el.removeAttribute('tabindex');
          el.removeAttribute('aria-hidden');
        });
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.querySelectorAll('a, button, select, input, textarea, [tabindex]').forEach((el) => {
          el.setAttribute('tabindex', '-1');
          el.setAttribute('aria-hidden', 'true');
        });
      }
    });

    // Remove keyboard focus from disabled nav buttons (WCAG 2.1.1)
    this.querySelectorAll('.swiper-button-next, .swiper-button-prev').forEach((btn) => {
      if (btn.classList.contains('swiper-button-disabled')) {
        btn.setAttribute('tabindex', '-1');
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.setAttribute('tabindex', '0');
        btn.removeAttribute('aria-disabled');
      }
    });
  }

  setupFocusScroll() {
    if (this.dataset.focusBound) return;
    this.dataset.focusBound = 'true';

    const getStickyHeaderHeight = () => {
      let maxBottom = 0;
      const selectors = [
        '#shopify-section-header-new',
        '.shopify-section.desk-navigation',
        'header.site-header',
        '.header-bottom',
        '.desk-navigation',
        '.page-anchor-nav',
        '.page-anchor-nav-section',
      ];

      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          const style = window.getComputedStyle(el);
          if (style.display === 'none' || style.visibility === 'hidden') return;
          const pos = style.position;
          const rect = el.getBoundingClientRect();
          if (rect.height < 1 || rect.width < 1) return;

          if (pos === 'fixed' || pos === 'sticky') {
            if (rect.bottom > 0) {
              maxBottom = Math.max(maxBottom, rect.bottom);
            }
          }
        });
      });

      return Math.max(90, Math.round(maxBottom));
    };

    this.addEventListener('focusin', (e) => {
      const focusTarget = e.target.closest(
        '.swiper-button-prev, .swiper-button-next, .swiper-pagination-bullet, a, button, input, select, [tabindex="0"]'
      );
      if (!focusTarget) return;

      requestAnimationFrame(() => {
        const rect = focusTarget.getBoundingClientRect();
        const stickyHeaderHeight = getStickyHeaderHeight();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        const isCutOffTop = rect.top < stickyHeaderHeight + 15;
        const isCutOffBottom = rect.bottom > viewportHeight - 15;

        if (isCutOffTop) {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetY = scrollTop + rect.top - stickyHeaderHeight - 25;
          window.scrollTo({
            top: Math.max(0, targetY),
            behavior: 'smooth',
          });
        } else if (isCutOffBottom) {
          focusTarget.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      });
    });
  }

  setupPaginationAccessibility() {
    const pagination = this.querySelector('.swiper-pagination');
    if (!pagination) return;

    pagination.setAttribute('role', 'tablist');
    if (!pagination.hasAttribute('aria-label')) {
      pagination.setAttribute('aria-label', 'Slideshow pagination');
    }

    const bullets = Array.from(pagination.querySelectorAll('.swiper-pagination-bullet'));
    const totalBullets = bullets.length;

    let activeIndex = bullets.findIndex((b) => b.classList.contains('swiper-pagination-bullet-active'));
    if (activeIndex === -1) activeIndex = 0;

    bullets.forEach((bullet, index) => {
      const isActive = index === activeIndex;

      bullet.setAttribute('role', 'tab');
      bullet.setAttribute('aria-label', `Slide ${index + 1}`);
      bullet.setAttribute('aria-posinset', (index + 1).toString());
      bullet.setAttribute('aria-setsize', totalBullets.toString());

      if (isActive) {
        bullet.classList.add('swiper-pagination-bullet-active');
        bullet.setAttribute('tabindex', '0');
        bullet.setAttribute('aria-selected', 'true');
        bullet.removeAttribute('aria-current');
      } else {
        bullet.classList.remove('swiper-pagination-bullet-active');
        bullet.setAttribute('tabindex', '-1');
        bullet.setAttribute('aria-selected', 'false');
        bullet.removeAttribute('aria-current');
      }
    });

    if (!pagination.dataset.keyboardBound) {
      pagination.dataset.keyboardBound = 'true';

      const activateBulletIndex = (targetIndex) => {
        const tabBullets = Array.from(pagination.querySelectorAll('.swiper-pagination-bullet[role="tab"]'));
        if (!tabBullets.length || targetIndex < 0 || targetIndex >= tabBullets.length) return;

        tabBullets.forEach((b, idx) => {
          if (idx === targetIndex) {
            b.classList.add('swiper-pagination-bullet-active');
            b.setAttribute('tabindex', '0');
            b.setAttribute('aria-selected', 'true');
          } else {
            b.classList.remove('swiper-pagination-bullet-active');
            b.setAttribute('tabindex', '-1');
            b.setAttribute('aria-selected', 'false');
          }
        });

        if (this.swiper) {
          const currentReal = this.swiper.params && this.swiper.params.loop ? this.swiper.realIndex : this.swiper.activeIndex;
          if (currentReal !== targetIndex) {
            if (this.swiper.params && this.swiper.params.loop) {
              this.swiper.slideToLoop(targetIndex);
            } else {
              this.swiper.slideTo(targetIndex);
            }
          }
        }
      };

      pagination.addEventListener('click', (e) => {
        const bullet = e.target.closest('.swiper-pagination-bullet');
        if (bullet) {
          const tabBullets = Array.from(pagination.querySelectorAll('.swiper-pagination-bullet[role="tab"]'));
          const targetIndex = tabBullets.indexOf(bullet);
          if (targetIndex !== -1) {
            activateBulletIndex(targetIndex);
          }
          bullet.focus();
        }
      });

      pagination.addEventListener('keydown', (e) => {
        const tabBullets = Array.from(pagination.querySelectorAll('.swiper-pagination-bullet[role="tab"]'));
        if (!tabBullets.length) return;

        const currentTarget = e.target.closest('.swiper-pagination-bullet');
        if (!currentTarget) return;

        const currentIndex = tabBullets.indexOf(currentTarget);
        if (currentIndex === -1) return;

        let targetIndex = null;

        if ((e.key === 'ArrowRight' || e.key === 'ArrowDown') && !e.ctrlKey && !e.altKey && !e.metaKey) {
          e.preventDefault();
          targetIndex = (currentIndex + 1) % tabBullets.length;
        } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowUp') && !e.ctrlKey && !e.altKey && !e.metaKey) {
          e.preventDefault();
          targetIndex = (currentIndex - 1 + tabBullets.length) % tabBullets.length;
        } else if (e.key === 'Home' && !e.ctrlKey && !e.altKey && !e.metaKey) {
          e.preventDefault();
          targetIndex = 0;
        } else if (e.key === 'End' && !e.ctrlKey && !e.altKey && !e.metaKey) {
          e.preventDefault();
          targetIndex = tabBullets.length - 1;
        } else if (e.key === 'Enter' || e.key === ' ') {
          if (currentTarget.tagName !== 'BUTTON') {
            e.preventDefault();
            currentTarget.click();
          }
          return;
        }

        if (targetIndex !== null) {
          const nextBullet = tabBullets[targetIndex];
          activateBulletIndex(targetIndex);
          nextBullet.focus();
        }
      });
    }
  }


  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
  }
}

if (!customElements.get('slideshow-container')) {
  customElements.define('slideshow-container', SlideshowContainer);
}

class SlideshowContainerItem extends HTMLElement {
  constructor() {
    super();
  }
}

if (!customElements.get('slideshow-container-item')) {
  customElements.define('slideshow-container-item', SlideshowContainerItem);
}